import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import ToastContainer from "../components/common/Toast";
import { playNotificationSound } from "../utils/notificationSound";

const ToastContext = createContext(null);

const TOAST_DURATION_MS = 5000;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const showToast = useCallback((message, type = "info", options = {}) => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, message, type, action: options?.action || null }]);
    // Play a soft chime alongside the toast (skippable via { silent: true }).
    // Honours the per-device mute toggle in Settings → Theme.
    if (!options?.silent) {
      playNotificationSound();
    }
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, TOAST_DURATION_MS);
  }, []);

  const dismissToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  // Memoize context value so consumers (AppInner) don't re-render every time
  // the toasts list changes — only when showToast itself changes (it never does).
  const contextValue = useMemo(() => ({ showToast }), [showToast]);

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used inside ToastProvider");
  return ctx;
}
